// src/utils/albumTracking.js
// Album creation tracking for free tier (localStorage based)

const STORAGE_KEY_COUNT = 'albumsCreatedThisMonth';
const STORAGE_KEY_RESET = 'monthlyResetDate';
const FREE_TIER_LIMIT = 2;

/**
 * Get the next month's first day
 */
function getNextMonthDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
}

/**
 * Check if we need to reset the counter
 */
function shouldResetCounter() {
  const resetDate = localStorage.getItem(STORAGE_KEY_RESET);
  if (!resetDate) return true;
  
  const now = new Date();
  const reset = new Date(resetDate);
  return now >= reset;
}

/**
 * Reset the counter to 0
 */
function resetCounter() {
  localStorage.setItem(STORAGE_KEY_COUNT, '0');
  localStorage.setItem(STORAGE_KEY_RESET, getNextMonthDate());
  console.log('[AlbumTracking] Counter reset for new month');
}

/**
 * Get current album count for this month
 */
export function getAlbumsCreatedThisMonth() {
  // Check if we need to reset
  if (shouldResetCounter()) {
    resetCounter();
    return 0;
  }
  
  const count = parseInt(localStorage.getItem(STORAGE_KEY_COUNT) || '0', 10);
  return count;
}

/**
 * Get how many albums remaining this month
 */
export function getAlbumsRemaining() {
  const created = getAlbumsCreatedThisMonth();
  const remaining = FREE_TIER_LIMIT - created;
  return Math.max(0, remaining);
}

/**
 * Check if user can create another album
 */
export function canCreateAlbum() {
  return getAlbumsRemaining() > 0;
}

/**
 * Increment the album creation counter
 * Returns the new count
 */
export function incrementAlbumCount() {
  // Check if we need to reset first
  if (shouldResetCounter()) {
    resetCounter();
  }
  
  const currentCount = getAlbumsCreatedThisMonth();
  const newCount = currentCount + 1;
  
  localStorage.setItem(STORAGE_KEY_COUNT, newCount.toString());
  console.log('[AlbumTracking] Album count incremented:', newCount);
  
  return newCount;
}

/**
 * Get the date when the counter will reset
 */
export function getResetDate() {
  const resetDate = localStorage.getItem(STORAGE_KEY_RESET);
  if (!resetDate) {
    const nextMonth = getNextMonthDate();
    localStorage.setItem(STORAGE_KEY_RESET, nextMonth);
    return new Date(nextMonth);
  }
  return new Date(resetDate);
}

/**
 * Format reset date for display
 */
export function getResetDateFormatted() {
  const resetDate = getResetDate();
  return resetDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Get album tracking status for UI display
 */
export function getAlbumTrackingStatus() {
  const created = getAlbumsCreatedThisMonth();
  const remaining = getAlbumsRemaining();
  const resetDate = getResetDateFormatted();
  const canCreate = canCreateAlbum();
  
  return {
    created,
    remaining,
    limit: FREE_TIER_LIMIT,
    resetDate,
    canCreate,
    percentage: (created / FREE_TIER_LIMIT) * 100,
  };
}

/**
 * Force reset (for testing only)
 */
export function forceResetForTesting() {
  if (process.env.NODE_ENV === 'development') {
    resetCounter();
    console.log('[AlbumTracking] 🧪 Force reset for testing');
  }
}
